/* eslint-disable react/prop-types */
import * as eventStyles from '../styles/events/event-page.module.scss';
import * as pageStyles from '../styles/internal-page/internal-page.module.scss';
import * as responsiveStyles from '../styles/responsive.module.scss';
import {GatsbyImage, getImage} from 'gatsby-plugin-image';
import {cloneDeep, map, pullAt} from 'lodash';
import {
  altNN,
  getImageAndAlt,
  getSEOInformations,
  mapSectionIntoMain,
  removeUselessWidgets,
} from '../utils/parsing';

import BottomLayout from '../components/layout/templates/BottomLayout';
import Content from '../components/page_structure/Content';
import EventLayout from '../components/layout/templates/EventLayout';
import MarketingButton from '../components/page_structure/MarketingButton';
import React from 'react';
import RelatedContent from '../components/page_structure/RelatedContent';
import Seo from '../seo/seo';
import Title from '../components/shared/Title';
import clsx from 'clsx';
import {graphql, Slice} from 'gatsby';
import {getI18n, useTranslation} from 'react-i18next';
import OpenedRelatedContents from '../components/page_structure/OpenedRelatedContents';
import '../services/i18n';
import { setupi18n } from '../utils/utils';

export const Head = ({ data, pageContext, location }) => {
  const {locale, generalSeo, websiteSeo, name, favicon, isScPoWebsite } = pageContext;
  const {
    canonical_link,
    pageSeo,
    metaDescription,
  } = getSEOInformations(data);
  const newsItem = data.page;
  const cover = getImageAndAlt(newsItem.cover, locale);

  return (
    <Seo
      canonicalLink={canonical_link}
      coverMedia={cover}
      favicon={favicon}
      generalSeo={generalSeo}
      isScPoWebsite={isScPoWebsite}
      language={locale}
      location={location}
      metaDescription={metaDescription}
      pageSeo={pageSeo}
      publishedAt={newsItem.publishedAt}
      type={pageContext.pageType}
      website={name}
      websiteSeo={websiteSeo}
    />
  );
};

export default function NewsItemPage ({data, pageContext}) {
  const {websiteRoot, breadcrumbs, locale} = pageContext;
  setupi18n(locale, getI18n());

  const newsItem = data.page;
  const cover = getImageAndAlt(newsItem.cover, locale);
  const displayCover = newsItem?.show_cover_media;
  const {t} = useTranslation();
  const [fullRelatedContents, setFullRelatedContents] = React.useState(false);

  const getRelatedContents = (contents) => {
    let actionElement = null;
    const relatedContents = map(contents, (item) => item.content[0]).filter(n => n);
    const prevRelatedContents = cloneDeep(relatedContents);

    if (
      relatedContents?.length > 0 &&
            relatedContents[0].__typename === 'CmsPortalComponentWidgetActionElement'
    ) {
      actionElement = relatedContents[0];
      pullAt(relatedContents, 0);
    }
    return {
      relatedContents: relatedContents,
      actionElement: actionElement,
      prevRelatedContents: prevRelatedContents,
    };
  };

  const {relatedContents, actionElement, prevRelatedContents} =
    getRelatedContents(newsItem.related_contents);

  const [newContent] = React.useState(
    removeUselessWidgets(mapSectionIntoMain(newsItem?.content))
  );
  const [newBottom] = React.useState(
    removeUselessWidgets(mapSectionIntoMain(newsItem?.bottom))
  );

  const innerTemplate = (
    <div className={eventStyles.RootDiv}>
      <div className={clsx(eventStyles.rootContent,
        {[pageStyles.rootItemNoRelatedContents]: relatedContents?.length <= 0,
          [pageStyles.rootItemRelatedContents]: relatedContents?.length > 0})}
      >
        <div
          className={clsx(pageStyles.marginBetweenBlocs, eventStyles.contentHeader)}
        >
          {cover && displayCover && (
            <div className={eventStyles.blocCover}>
              <div className={eventStyles.innerBloc}>
                <GatsbyImage
                  alt={altNN('')}
                  className={pageStyles.cover}
                  image={getImage(cover.image)}
                  loading="eager"
                  role={'none'}
                />
              </div>
            </div>
          )}
          {newsItem?.title && cover && displayCover && (
            <div className={eventStyles.divTitre}>
              <div className={eventStyles.blocTitre}>
                <div className={eventStyles.innerBlocTitre}>
                  <Title
                    color="RED"
                    date={newsItem.publishedAt}
                    label={newsItem.title}
                    size={35}
                  />
                </div>
              </div>
            </div>
          )}
          {newsItem?.title && (!cover || !displayCover) && (
            <Title
              color="RED"
              date={newsItem.publishedAt}
              label={newsItem.title}
              separator
              size={35}
            />
          )}
          <div
            className={
              cover && newsItem?.title && displayCover
                ? eventStyles.espacement
                : ''
            }
            style={{width: 'inherit'}}
          >
            {newContent?.map((content, index) => {
              return (
                <Content
                  elem={content}
                  fullWidth={relatedContents?.length <= 0}
                  key={'content-smartphone-' + index}
                  locale={locale}
                />
              );
            })}
            {relatedContents?.length > 0 && relatedContents[0]?.related_contents && (
              <MarketingButton
                actionElement={relatedContents[0]?.related_contents[0]?.content[0]}
                className={responsiveStyles.smartphone}
                knowMore={t('internal.page.more')}
                mobile
                onclick={() => {
                  window.scrollTo(0, 0);
                  setFullRelatedContents(true);
                }}
              />
            )}
            {displayCover && cover && (
              <p
                className={eventStyles.author}
              >
                {cover.legend}
              </p>
            )}
          </div>
        </div>
      </div>
      {prevRelatedContents?.length > 0 && (
        <div className={eventStyles.relatedContentDesktop}>
          <div style={{minWidth: '16%'}}></div>
          <div style={{maxWidth: '84%'}}>
            <div
              className={eventStyles.contentRelated}
            >
              {prevRelatedContents?.map((elem, index) => {
                return (
                  <RelatedContent elem={elem}
                    key={index}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>);

  return (
    <Slice alias={'internal-page-layout'}>
      {fullRelatedContents ? (
        <OpenedRelatedContents
          handleClickBack={() => setFullRelatedContents(false)}
          related_contents={relatedContents[0].related_contents}
        />
      ) : (
        <>
          <EventLayout filAriane={breadcrumbs}
            url={websiteRoot}
          >
            {innerTemplate}
            {newsItem?.authors?.length > 0 && (
              <p className={pageStyles.authorStyle}>
                {t('authors.written.by')}
                {newsItem.authors.map(({name, email}, i) => (
                  <>
                    <a href={'mailto:' + email}
                      key={i}
                    >
                      {name}
                    </a>
                    {i < newsItem.authors.length - 1 ? ', ' : ''}
                  </>
                ))}
              </p>
            )}
          </EventLayout>
          <div className={clsx(pageStyles.marginBetweenBlocs, eventStyles.contentBottomLayout)}>
            <BottomLayout>
              {newBottom?.map((content, index) => {
                return (
                  <Content
                    elem={content}
                    fullWidth
                    key={'bottom-' + index}
                    locale={locale}
                    siteURL={websiteRoot}
                  />
                );
              })}
            </BottomLayout>
          </div>
        </>
      )
      }
    </Slice>
  );
}

export const query = graphql`
  query ($newsId: String!) {
    page: cmsPortalNewsItem(id: { eq: $newsId }) {
      ...NewsData
      content {
        ...NewsContent
      }
      related_contents {
        ...RelatedContentsFragment
      }
      bottom {
        ...NewsContent
      }
    }
  }
`;
